import React from 'react';
import { Rect, Group, Text, Line } from 'react-konva';
import {
  NAVY, ORANGE, ROOM_FILL_DEFAULT, ROOM_FILL_OK,
  WALL_THICKNESS,
} from '../constants';

/**
 * Renders a single room on the canvas.
 *
 * Two modes:
 * - POLYGON: room.vertices exists → renders closed Line shape
 * - RECT (fallback): uses x/y/w/h bounding box → renders Rect
 *
 * Both show: name label, area, check mark, selection highlight.
 */
export default function RoomShape({
  room,
  index,
  x, y, w, h,
  isSelected,
  editMode,
  onSelect,
  onRightClick,
  plantX,  // plant origin X (for polygon mode)
  plantY,  // plant origin Y (for polygon mode)
  sc,      // scale factor (for polygon mode)
}) {
  const fill = room.ok ? ROOM_FILL_OK : ROOM_FILL_DEFAULT;
  const stroke = isSelected ? ORANGE : 'transparent';
  const strokeWidth = isSelected ? 2 : 0;

  const nameText = room.nome || 'Cômodo';
  const areaText = `${room.area || '?'} m²`;

  const minDim = Math.min(w, h);
  const maxChars = Math.max(3, Math.floor(w / 7));
  const displayName = nameText.length > maxChars
    ? nameText.substring(0, maxChars - 1) + '…'
    : nameText;

  const nameFontSize = Math.max(8, Math.min(13, minDim / 5, w / 8));
  const areaFontSize = Math.max(6, Math.min(10, nameFontSize - 2));
  const showArea = minDim > 35;

  const handleClick = (e) => {
    e.cancelBubble = true;
    onSelect && onSelect(index);
  };

  const handleContext = (e) => {
    e.evt.preventDefault();
    e.cancelBubble = true;
    onRightClick && onRightClick(index, e.evt.clientX, e.evt.clientY);
  };

  // ── POLYGON MODE ──
  if (room.vertices && room.vertices.length >= 3 && plantX != null && sc) {
    // Convert virtual vertices to canvas pixels
    const points = room.vertices.flatMap(([vx, vy]) => [
      plantX + vx * sc,
      plantY + vy * sc,
    ]);

    // Center of bounding box for text placement
    const cx = x + w / 2;
    const cy = y + h / 2;

    return (
      <Group onClick={handleClick} onTap={handleClick} onContextMenu={handleContext}>
        {/* Polygon fill */}
        <Line
          points={points}
          closed
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
        />

        {/* Room name */}
        <Text
          x={cx - w / 2}
          y={cy - nameFontSize - 2}
          width={w}
          text={displayName}
          fontSize={nameFontSize}
          fontFamily="Inter, sans-serif"
          fontStyle="700"
          fill={NAVY}
          align="center"
          listening={false}
        />

        {/* Area */}
        {showArea && (
          <Text
            x={cx - w / 2}
            y={cy + 2}
            width={w}
            text={areaText}
            fontSize={areaFontSize}
            fontFamily="Inter, sans-serif"
            fill="#6B7280"
            align="center"
            listening={false}
          />
        )}

        {/* Check mark */}
        {room.ok && (
          <Text
            x={x + w - 18}
            y={y + 4}
            text="✓"
            fontSize={12}
            fontFamily="Inter, sans-serif"
            fontStyle="700"
            fill="#22C55E"
            listening={false}
          />
        )}
      </Group>
    );
  }

  // ── RECT MODE (fallback) ──
  const wt = WALL_THICKNESS / 2;

  return (
    <Group
      x={x + wt}
      y={y + wt}
      onClick={handleClick}
      onTap={handleClick}
      onContextMenu={handleContext}
    >
      <Rect
        width={w - WALL_THICKNESS}
        height={h - WALL_THICKNESS}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        cornerRadius={0}
      />

      <Text
        x={0}
        y={(h - WALL_THICKNESS) / 2 - nameFontSize - 2}
        width={w - WALL_THICKNESS}
        text={displayName}
        fontSize={nameFontSize}
        fontFamily="Inter, sans-serif"
        fontStyle="700"
        fill={NAVY}
        align="center"
        listening={false}
      />

      {showArea && (
        <Text
          x={0}
          y={(h - WALL_THICKNESS) / 2 + 2}
          width={w - WALL_THICKNESS}
          text={areaText}
          fontSize={areaFontSize}
          fontFamily="Inter, sans-serif"
          fill="#6B7280"
          align="center"
          listening={false}
        />
      )}

      {room.ok && (
        <Text
          x={w - WALL_THICKNESS - 18}
          y={4}
          text="✓"
          fontSize={12}
          fontFamily="Inter, sans-serif"
          fontStyle="700"
          fill="#22C55E"
          listening={false}
        />
      )}
    </Group>
  );
}
